import React, { createContext, useContext, useState, useEffect } from "react";
import {
  useOrganization,
  useOrganizationList,
  useUser,
  UserButton,
} from "@clerk/clerk-react";
import { Activity, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { CustomCreateOrganization } from "../components/CustomCreateOrganization";

const OrganizationContext = createContext();

export function OrganizationProvider({ children }) {
  const { organization: clerkOrganization, membership, isLoaded: orgLoaded } = useOrganization();
  const {
    organizationList,
    userMemberships,
    setActive,
    isLoaded: listLoaded,
  } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });
  const { user, isLoaded: userLoaded } = useUser();
  const [orgReady, setOrgReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);
  const [automatedOrg, setAutomatedOrg] = useState(null);

  // Enhanced debugging
  // console.log("OrganizationProvider Debug:", {
  //   orgLoaded,
  //   listLoaded,
  //   userLoaded,
  //   organizationList,
  //   userMemberships,
  //   organization,
  //   userMembershipsData: userMemberships?.data,
  //   userMembershipsCount: userMemberships?.count,
  // });
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const apiKey = params.get('api_key');
    const urlOrgId = params.get('org_id');

    if (apiKey && urlOrgId) {
      // If we have an API key, we don't need to wait for Clerk's org list
      setAutomatedOrg({ id: urlOrgId, name: "Automated Report" });
      setIsInitializing(false);
      setOrgReady(true);
    } else if (userLoaded && orgLoaded && listLoaded) {
      // Wait for all loading states AND ensure we have attempted to fetch organization data
      const timer = setTimeout(() => {
        setIsInitializing(false);
        setOrgReady(Boolean(organization));
      }, 300); // Increased delay to ensure data is ready

      return () => clearTimeout(timer);
    }
  }, [
    userLoaded,
    orgLoaded,
    listLoaded,
    organization,
    organizationList,
    userMemberships,
  ]);
  const switchOrganization = async (orgId) => {
    try {
      setOrgReady(false);
      await setActive({ organization: orgId });
    } catch (error) {
      console.error("Switch organization error:", error);
      toast.error("Failed to switch organization");
      throw new Error();
    }
  };

  const getCurrentRole = () => {
    if (!membership?.role) return "member";
    return /admin|creator|owner/i.test(membership.role)
      ? "admin"
      : membership.role.toLowerCase();
  };

  const isAdmin = () =>
    Boolean(membership?.role && /admin|creator|owner/i.test(membership.role));

  // Use userMemberships.data if organizationList is not available
  const effectiveOrganizationList =
    organizationList || userMemberships?.data || [];
  const organization = automatedOrg || clerkOrganization;
  const value = {
    organization,
    organizationList: effectiveOrganizationList,
    userMemberships,
    orgReady,
    isInitializing,
    isLoaded: userLoaded && orgLoaded && listLoaded,
    switchOrganization,
    getCurrentRole,
    isAdmin,
    user,
    isCreatingOrganization,
    setIsCreatingOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganizationContext() {
  const context = useContext(OrganizationContext);
  if (!context)
    throw new Error(
      "useOrganizationContext must be used within an OrganizationProvider"
    );
  return context;
}

// Custom hook for organization creation state
export function useOrganizationCreation() {
  const { isCreatingOrganization, setIsCreatingOrganization } = useOrganizationContext();
  return { isCreatingOrganization, setIsCreatingOrganization };
}

// Small helper to show a user profile/logout button fixed at the top-right
function TopRightUserButton() {
  return (
    <div className="fixed top-4 right-4 z-[60]">
      <UserButton afterSignOutUrl="/" />
    </div>
  );
}

export function OrganizationGate({ children }) {
  const {
    isInitializing,
    isLoaded,
    organizationList,
    organization,
    userMemberships,
    isCreatingOrganization,
  } = useOrganizationContext();

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex items-center justify-center p-4 py-16">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-8">
          <div className="text-center">
            <div className="mx-auto mb-6 w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-700 shadow-lg">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain animate-pulse" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Uttik Platform</h2>
            <p className="text-gray-600">Preparing your workspace...</p>
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-75"></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-150"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check both organizationList and userMemberships
  const hasOrganizations =
    organizationList.length > 0 ||
    (userMemberships?.data && userMemberships.data.length > 0);

  // if (isLoaded && !hasOrganizations) {
  //   return <CreateFirstOrganization />;
  // }

  const params = new URLSearchParams(window.location.search);
  const isAutomated = params.get('automated') === 'true';

  if (isLoaded && !organization && !isAutomated) {
    return <SelectOrganization />;
  }

  // Show loading state if organization creation is in progress
  if (isCreatingOrganization) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex items-center justify-center p-4 py-16">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-8">
          <div className="text-center">
            <div className="mx-auto mb-6 w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-700 shadow-lg">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain animate-pulse" />
            </div>
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Setting up your organization...</h2>
            <p className="text-gray-600">This may take a few moments</p>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

export function CreateFirstOrganization() {
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex items-center justify-center overflow-y-auto py-16 px-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="mx-auto mb-4 w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-700 shadow-lg">
            <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            Welcome to <span className="text-purple-600">Uttik Platform</span>
          </h2>
          <p className="text-gray-600 mt-2 text-center">
            Create your first organization to get started
          </p>
        </div>
        <CustomCreateOrganization 
          isModal={false}
          onSuccess={(clerkOrg) => {
            // Don't reload, just let the context handle the transition
            // console.log("Organization created:", clerkOrg);
          }}
        />
      </div>
    </div>
  );
}

export function SelectOrganization() {
  const { organizationList, userMemberships, isLoaded, switchOrganization } =
    useOrganizationContext();

  // Enhanced debugging
  // console.log("SelectOrganization Debug:", {
  //   isLoaded,
  //   organizationList,
  //   userMemberships,
  //   userMembershipsData: userMemberships?.data,
  //   organizationListLength: organizationList?.length,
  //   userMembershipsCount: userMemberships?.count,
  // });
  // Enhanced loading check - wait for both isLoaded AND data to be ready
  const isDataReady =
    isLoaded &&
    ((organizationList !== undefined && Array.isArray(organizationList)) ||
      (userMemberships !== undefined && userMemberships.data !== undefined));

  // Show loading spinner while data is being fetched
  if (!isDataReady) {
    return (
      <>
        <TopRightUserButton />
        <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex flex-col items-center justify-center p-4 py-16">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-8">
            <div className="text-center">
              <div className="mx-auto mb-6 w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-700 shadow-lg">
                <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain animate-pulse" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading Uttik Platform</h2>
              <p className="text-gray-600">Preparing your workspace...</p>
              <div className="mt-6 flex justify-center">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-75"></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-150"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Build effective org list and flags
  let orgs = [];
  if (organizationList && organizationList.length > 0) {
    orgs = organizationList;
  } else if (userMemberships?.data && userMemberships.data.length > 0) {
    orgs = userMemberships.data;
  }
  const hasOrganizations = orgs.length > 0;

  // Additional safety check - if we just loaded and have no data, wait a bit more
  const [hasWaited, setHasWaited] = React.useState(false);

  React.useEffect(() => {
    if (isDataReady && !hasOrganizations) {
      const timer = setTimeout(() => {
        setHasWaited(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setHasWaited(true);
    }
  }, [isDataReady, hasOrganizations]);

  // Show loading if we haven't waited enough and have no organizations
  if (!hasWaited && !hasOrganizations) {
    return (
      <>
        <TopRightUserButton />
        <div className="min-h-screen bg-gradient-to-br from-grey-600 via-grey-700 to-grey-800 flex flex-col items-center justify-center p-4 py-16">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-8">
            <div className="text-center">
              <div className="mx-auto mb-6 w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-grey-600 to-grey-700 shadow-lg">
                <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain animate-pulse" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading Organizations</h2>
              <p className="text-gray-600">Discovering your workspaces...</p>
              <div className="mt-6 flex justify-center">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-75"></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-150"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <TopRightUserButton />
      <div className="min-h-screen bg-gradient-to-br from-grey-600 via-grey-700 to-grey-800 flex flex-col items-center justify-center p-4 py-16 overflow-y-auto">
      {/* Main Card Container */}
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-6 w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-grey-600 to-grey-700 shadow-lg">
            <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Choose Your <span className="text-purple-600">Organization</span>
          </h1>
          <p className="text-gray-600 leading-relaxed">
            Select an organization to access your dashboard and manage your projects with enhanced productivity tools.
          </p>
        </div>

        {/* Organizations List or Welcome Message */}
        {hasOrganizations ? (
          <div className="space-y-3 mb-8">
            {orgs.map((item) => {
              const org = item.organization || item;
              const membership = item.membership || item;

              if (!org || !org.id) {
                console.warn("Invalid organization item:", item);
                return null;
              }

              return (
                <div
                  key={org.id}
                  onClick={() => switchOrganization(org.id)}
                  className="group cursor-pointer bg-gray-50 hover:bg-purple-50 rounded-xl p-4 transition-all duration-300 transform hover:scale-[1.02] border border-gray-100 hover:border-purple-200"
                >
                  <div className="flex items-center gap-3">
                    {org.imageUrl ? (
                      <img
                        src={org.imageUrl}
                        alt={org.name}
                        className="w-12 h-12 rounded-lg object-cover shadow-sm"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
                        <span className="text-white font-bold text-lg">
                          {org.name?.charAt(0)?.toUpperCase() || "?"}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                        {org.name || "Unnamed Organization"}
                      </h3>
                      <p className="text-sm text-gray-500 capitalize">
                        {membership?.role || "member"}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Welcome to Uttik Platform
            </h3>
            <p className="text-gray-600 leading-relaxed">
              You don't have access to any organizations yet. Create your first one to get started on your journey and unlock powerful collaboration features.
            </p>
          </div>
        )}

        {/* Create Organization Section */}
        <div className="text-center">
          <p className="text-gray-600 mb-6">
            {hasOrganizations ? "Need a new workspace?" : "Ready to begin your journey?"}
          </p>
          <CustomCreateOrganization
            onSuccess={(clerkOrg) => {
              // Don't reload, just let the context handle the transition
              // console.log("Organization created:", clerkOrg);
            }}
          >
            <button
              type="button"
              className="w-full inline-flex items-center justify-center px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 hover:from-purple-700 hover:to-indigo-800"
            >
              <Plus className="h-5 w-5 mr-2" />
              {hasOrganizations
                ? "Create New Organization"
                : "Create Your First Organization"}
            </button>
          </CustomCreateOrganization>
        </div>
      </div>
      </div>
    </>
  );
}
